import { CombinedError } from 'urql';

/**
 * Anything we might receive on an error path in the bill manager router:
 *  - `routeError` from `useQuery` is a raw urql {@link CombinedError} and still
 *    carries `networkError` / `graphQLErrors`.
 *  - `cardsError` from `useFetchCardsWithLockStatus` has already been passed
 *    through `useFormattedGQLErrors`, so it arrives as a plain `Error`-like
 *    object (a `GenericError` with `name`/`message`/`code`/`title`) and the
 *    raw network/GraphQL detail has been collapsed away.
 */
type SerializableError =
  | CombinedError
  | (Error & { code?: string; title?: string });

const hasGraphQLErrors = (error: SerializableError): error is CombinedError =>
  Array.isArray((error as CombinedError).graphQLErrors);

/**
 * Flattens an error from a bill manager router query into primitive,
 * analytics-friendly fields so a failure can be fully diagnosed from the
 * logged event.
 *
 * The most important signal this surfaces is the distinction between a real
 * server/GraphQL failure and a client-side request teardown (abort):
 *  - A genuine GraphQL error has `graphql_error_count > 0`.
 *  - A client teardown / cancelled fetch has `network_error_name: 'AbortError'`
 *    with `graphql_error_count: 0`.
 *
 * Note: this signature is only fully observable on the raw `CombinedError`
 * (e.g. the route query error). Formatted errors carry only the generic
 * `error_name` / `error_message` / `error_code` fields.
 */
export const serializeQueryError = (
  error?: SerializableError | null,
): Record<string, string | number | boolean | undefined> => {
  if (!error) {
    return {};
  }

  const base = {
    error_name: error.name,
    error_message: error.message,
    error_code: (error as { code?: string }).code,
    error_title: (error as { title?: string }).title,
  };

  if (!hasGraphQLErrors(error)) {
    return base;
  }

  const networkError = error.networkError;
  const graphQLErrors = error.graphQLErrors ?? [];
  const response = error.response as
    | { status?: number; statusText?: string }
    | undefined;

  return {
    ...base,
    network_error_name: networkError?.name,
    network_error_message: networkError?.message,
    graphql_error_count: graphQLErrors.length,
    graphql_error_messages:
      graphQLErrors.map((graphQLError) => graphQLError.message).join('; ') ||
      undefined,
    response_status: response?.status,
    response_status_text: response?.statusText,
  };
};
